import React, { useEffect, useMemo, useState } from 'react';
import { DatePicker, Input } from '../../components/common';
import { Search } from 'iconoir-react';
import { MATCH_STATUS } from '../../types/match';
import {
  ApprovedAgentReviewListing,
  RejectedAgentReviewListing,
} from '../../components/dashboard';
import { useAgentReviewCounts } from '../../hooks/useAgentReview';

const AgentReview = () => {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<string>(MATCH_STATUS.APPROVED);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const { data: agentReviewCounts, refetch: fetchAgentReviewCounts } =
    useAgentReviewCounts({
      start_date: startDate?.toISOString(),
      end_date: endDate?.toISOString(),
    });

  const tabs = useMemo<{ label: string; value: string; count: number }[]>(
    () => [
      {
        label: 'Pursued',
        value: MATCH_STATUS.APPROVED,
        count: agentReviewCounts?.result?.approved ?? 0,
      },
      {
        label: 'Not Pursued',
        value: MATCH_STATUS.REJECTED,
        count: agentReviewCounts?.result?.passed ?? 0,
      },
    ],
    [agentReviewCounts]
  );

  const onDateChange = (dates: Date | [Date | null, Date | null] | null) => {
    if (Array.isArray(dates)) {
      const [start, end] = dates;
      setStartDate(start);
      setEndDate(end);
      setSearch('');
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  useEffect(() => {
    fetchAgentReviewCounts();
  }, [endDate]);

  return (
    <div className='px-4 py-6'>
      <div className='border-2 border-purple-25 bg-gray-light-50 rounded-xl sm:p-6 p-4'>
        <div className='mb-6'>
          <div className='mb-6'>
            <h5 className='!font-medium text-heading-500 mb-1'>
              AI agent review
            </h5>
            <p className='text-sm text-body-400 font-medium mb-0'>
              Recent matches approved or rejected by your AI agent
            </p>
          </div>
          <Input
            wrapperClassName='max-w-[40.625rem] w-full'
            type='text'
            placeholder='Please enter the name to search'
            prefix={<Search className='text-primary-500 w-5 h-5' />}
            value={search}
            onChange={handleSearch}
          />
        </div>

        <div className='flex md:flex-row flex-col items-center justify-between mb-6 overflow-x-hidden'>
          <div className='flex items-center gap-2 order-2 md:order-1 whitespace-nowrap overflow-x-auto scrollbar-hide w-full md:w-fit'>
            {tabs.map(tab => (
              <div
                className={`py-2 px-4 rounded-lg text-xs font-semibold min-h-10 flex items-center justify-center cursor-pointer duration-200 text-body-700 ${activeTab === tab.value ? 'bg-theme-blue-500' : ''}`}
                key={tab.value}
                onClick={() => {
                  setActiveTab(tab.value);
                }}
              >
                <span className='leading-[1rem] mx-2'>{tab.label}</span>
                <span
                  className={`text-sm text-body-500 min-w-7 h-7 flex items-center justify-center rounded-md bg-white`}
                >
                  {tab.count}
                </span>
              </div>
            ))}
          </div>
          {/* Jun-18 (a user V1 test, R2-1 follow-up): "Once a date is selected
              how do I remove it?" The Jun-17 isClearable fix was applied to the
              Matches page (NetworkMatches) but MISSED this Agent Review picker,
              so a selected range here could not be cleared. Added isClearable to
              match; onDateChange already handles the [null, null] clear. */}
          <DatePicker
            wrapperClassName='w-full md:w-fit order-1 md:order-2 md:mb-0 mb-5'
            startDate={startDate}
            endDate={endDate}
            selectsRange
            isClearable
            placeholderText='Select Dates'
            onChange={onDateChange}
          />
        </div>

        <div>
          {activeTab === MATCH_STATUS.APPROVED && (
            <ApprovedAgentReviewListing
              search={search}
              startDate={startDate}
              endDate={endDate}
            />
          )}
          {activeTab === MATCH_STATUS.REJECTED && (
            <RejectedAgentReviewListing
              search={search}
              startDate={startDate}
              endDate={endDate}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentReview;
